import {
  isServiceCodeLineItem,
  toSingleClaimItems,
} from '@principle-theorem/principle-core';
import {
  AccInvoiceBillingMethod,
  IAccInvoice,
  IAccInvoiceBuilderClaimData,
  IAccLineItem,
  IClaimableItem,
  IPractice,
  IPracticeAccSettings,
} from '@principle-theorem/principle-core/interfaces';
import {
  ISODateType,
  omitEmptyValues,
  toISODate,
  WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';

export class AccInvoiceBuilder {
  static build(
    claimData: IAccInvoiceBuilderClaimData,
    claimItems: IClaimableItem[],
    practice: WithRef<IPractice>,
    accSettings: IPracticeAccSettings,
    serviceDate: ISODateType,
    appVersion: string
  ): IAccInvoice {
    const invoiceData = {
      pmsSoftwareName: 'Principle',
      pmsSoftwareVersion: appVersion,
      vendor: {
        vendorID: accSettings.vendorId,
        vendorName: practice.name,
        vendorGST: accSettings.vendorGST,
      },
      declaration: {
        declarationDate: toISODate(moment()),
      },
      lines: AccInvoiceBuilder._buildInvoiceLines(
        claimData,
        claimItems,
        serviceDate
      ),
      comment: AccInvoiceBuilder._buildComment(claimItems),
    };
    return omitEmptyValues(invoiceData) as IAccInvoice;
  }

  private static _buildInvoiceLines(
    claimData: IAccInvoiceBuilderClaimData,
    claimItems: IClaimableItem[],
    serviceDate: ISODateType
  ): IAccLineItem[] {
    const singleClaimItems = toSingleClaimItems(claimItems);

    return singleClaimItems.map((claimItem) =>
      AccInvoiceBuilder._claimItemToLineItem(claimItem, claimData, serviceDate)
    );
  }

  private static _claimItemToLineItem(
    claimItem: IClaimableItem,
    claimData: IAccInvoiceBuilderClaimData,
    serviceDate: ISODateType
  ): IAccLineItem {
    return {
      claimId: claimData.claimNumber,
      accidentDate: claimData.accidentDate,
      lineDetails: {
        serviceCodes: [claimItem.serviceCode.code],
        serviceDate,
        invoiceAmount: claimItem.serviceCode.amount,
        purchaseOrderNumber: claimData.purchaseOrderNumber,
        billing: {
          billingMethod: AccInvoiceBillingMethod.Units,
          unitsClaimed: 1,
        },
      },
      patient: claimData.patientDetails,
      provider: claimData.providerDetails,
    };
  }

  private static _buildComment(claimItems: IClaimableItem[]): string {
    const serviceCodes = claimItems
      .flatMap((claimItem) => claimItem.treatment.items)
      .filter((lineItem) => isServiceCodeLineItem(lineItem));

    const codesWithToothIds = serviceCodes
      .map((code) =>
        code.toothId ? `${code.code}(${code.toothId})` : code.code
      )
      .join(', ');

    const comment = `Service Codes: ${codesWithToothIds}`;
    return comment.slice(0, 255);
  }
}
